import { useState } from 'react'
import { useI18n } from '../../i18n'
import { SECTION_TYPES, INSTRUMENT_TYPES } from '../../config/songbook'
import { GuitarTab } from './GuitarTab'
import { GuitarTablature } from './GuitarTablature'
import { IMAGE_MAX_HEIGHT } from '../../config/songbook'
import { useCachedImage } from '../../lib/offline/useCachedImage'
import { MidiPlayer } from './MidiPlayer'
import type { SongElement, SectionElement, InstrumentTrack, ChordElement, TabElement, TablatureElement, ImageElement, MidiElement, TabDot, TablatureColumn } from '../../types/songbook'

interface SectionBlockProps {
  section: SectionElement
  tracks?: InstrumentTrack[]
  isActive?: boolean
  readOnly?: boolean
  onSelect?: () => void
  canMoveUp: boolean
  canMoveDown: boolean
  onDeleteElement: (id: string) => void
  onMoveSection: (sectionId: string, direction: 'up' | 'down') => void
  onUpdateElement: (id: string, data: Partial<SongElement>) => void
  onUpdateDots: (id: string, dots: TabDot[]) => void
  onUpdateColumns: (id: string, columns: TablatureColumn[]) => void
}

export function SectionBlock({
  section,
  tracks,
  isActive,
  readOnly,
  onSelect,
  canMoveUp,
  canMoveDown,
  onDeleteElement,
  onMoveSection,
  onUpdateElement,
  onUpdateDots,
  onUpdateColumns,
}: SectionBlockProps) {
  const { t } = useI18n()
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const collapsed = section.collapsed ?? false

  const [editingLabel, setEditingLabel] = useState(false)

  const cfg = SECTION_TYPES.find((s) => s.id === section.sectionTypeId)
  const color = cfg?.color ?? 'bg-surface-lighter'
  const defaultLabel = cfg ? t(cfg.i18nKey as Parameters<typeof t>[0]) : section.sectionTypeId
  const label = section.customName || defaultLabel

  // Only show the collapse arrow when expanding actually reveals something:
  // a named instrument track, or the default track holding content. Same
  // rule as the collapsed-state content badges below.
  const hasContent = (tracks ?? []).some(
    (tr) => tr.instrument.instrumentTypeId !== '_default' || tr.children.length > 0
  )

  return (
    <div className={`rounded-lg border bg-surface-light ${isActive ? 'border-primary' : 'border-surface-lighter'}`}>
      {/* Section header */}
      <div className="flex items-center justify-between p-3 pb-1">
        <div className="flex-1 cursor-pointer" onClick={() => { onSelect?.(); onUpdateElement(section.id, { collapsed: !collapsed } as Partial<SongElement>) }}>
          {!readOnly && editingLabel ? (
            <input
              type="text"
              defaultValue={label}
              placeholder={defaultLabel}
              className="w-40 rounded border border-primary bg-surface px-2 py-0.5 text-sm font-semibold text-text focus:outline-none"
              autoFocus
              onBlur={(e) => {
                const val = e.target.value.trim()
                onUpdateElement(section.id, {
                  customName: val && val !== defaultLabel ? val : undefined,
                } as Partial<SongElement>)
                setEditingLabel(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                if (e.key === 'Escape') setEditingLabel(false)
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span
                className={`inline-block rounded px-2 py-0.5 text-sm font-semibold text-white ${color}`}
                onDoubleClick={readOnly ? undefined : (e) => { e.stopPropagation(); setEditingLabel(true) }}
              >
                {hasContent && (
                  <span className="mr-1 inline-block text-[10px] transition-transform" style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                )}
                {label}
              </span>
              {collapsed && <SectionContentBadges tracks={tracks ?? []} t={t} />}
            </span>
          )}
          {section.note && editingNoteId !== section.id && (
            <p
              className="mt-1 cursor-pointer text-xs italic text-text-muted hover:text-text"
              onClick={() => setEditingNoteId(section.id)}
            >
              {section.note}
            </p>
          )}
          {editingNoteId === section.id && (
            <NoteInput
              value={section.note ?? ''}
              onChange={(v) => onUpdateElement(section.id, { note: v })}
              onClose={() => setEditingNoteId(null)}
              t={t}
            />
          )}
        </div>
        {!readOnly && <ActionIcons
          onNote={() => setEditingNoteId(editingNoteId === section.id ? null : section.id)}
          onMoveUp={() => onMoveSection(section.id, 'up')}
          onMoveDown={() => onMoveSection(section.id, 'down')}
          onDelete={() => onDeleteElement(section.id)}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
        />}
      </div>

      {/* Instrument tracks (collapsible) */}
      {!collapsed && (tracks ?? []).map((track) => (
        <InstrumentTrackRow
          key={track.instrument.id}
          track={track}
          readOnly={readOnly}
          editingNoteId={editingNoteId}
          onSetEditingNote={setEditingNoteId}
          onDeleteElement={onDeleteElement}
          onUpdateElement={onUpdateElement}
          onUpdateDots={onUpdateDots}
          onUpdateColumns={onUpdateColumns}
          t={t}
        />
      ))}

      {!collapsed && (tracks ?? []).length === 0 && (
        <div className="px-3 pb-2" />
      )}
    </div>
  )
}

/* ── Instrument Track Row ── */

function InstrumentTrackRow({
  track,
  readOnly,
  editingNoteId,
  onSetEditingNote,
  onDeleteElement,
  onUpdateElement,
  onUpdateDots,
  onUpdateColumns,
  t,
}: {
  track: InstrumentTrack
  readOnly?: boolean
  editingNoteId: string | null
  onSetEditingNote: (id: string | null) => void
  onDeleteElement: (id: string) => void
  onUpdateElement: (id: string, data: Partial<SongElement>) => void
  onUpdateDots: (id: string, dots: TabDot[]) => void
  onUpdateColumns: (id: string, columns: TablatureColumn[]) => void
  t: ReturnType<typeof useI18n>['t']
}) {
  const { instrument, children } = track
  const isDefault = instrument.instrumentTypeId === '_default'
  const instCfg = INSTRUMENT_TYPES.find((i) => i.id === instrument.instrumentTypeId)
  const instLabel = instrument.customName
    || (instCfg ? t(instCfg.i18nKey as Parameters<typeof t>[0]) : instrument.instrumentTypeId)

  return (
    <div className={`${isDefault ? '' : 'border-t border-surface-lighter/50'} px-3 py-2`}>
      {/* Instrument header (hidden for default/implicit tracks) */}
      {!isDefault && <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-text-muted">
            <span className="font-medium text-text">{instLabel}</span>
          </span>
          {instrument.note && editingNoteId !== instrument.id && (
            <span
              className="cursor-pointer text-[15px] italic text-text-muted hover:text-text"
              onClick={() => onSetEditingNote(instrument.id)}
            >
              — {instrument.note}
            </span>
          )}
        </div>
        {!readOnly && <div className="flex items-center gap-0.5">
          <button
            className="rounded p-0.5 text-text-muted hover:text-text"
            onClick={() => onSetEditingNote(editingNoteId === instrument.id ? null : instrument.id)}
          >
            <PenIcon size={12} />
          </button>
          <button
            className="rounded p-0.5 text-text-muted hover:text-error"
            onClick={() => onDeleteElement(instrument.id)}
          >
            <XIcon size={12} />
          </button>
        </div>}
      </div>}
      {readOnly && !isDefault && instrument.note && (
        <p className="mb-1 text-[15px] italic text-text-muted">{instrument.note}</p>
      )}

      {!readOnly && !isDefault && editingNoteId === instrument.id && (
        <NoteInput
          value={instrument.note ?? ''}
          onChange={(v) => onUpdateElement(instrument.id, { note: v })}
          onClose={() => onSetEditingNote(null)}
          t={t}
        />
      )}

      {/* Horizontally scrollable chords/tabs */}
      {children.length > 0 && (
        <div className="flex items-start gap-2 overflow-x-auto pb-1 scrollbar-none">
          {children.map((child) => (
            <ChildElement
              key={child.id}
              element={child}
              readOnly={readOnly}
              editingNoteId={editingNoteId}
              onSetEditingNote={onSetEditingNote}
              onDelete={() => onDeleteElement(child.id)}
              onUpdateElement={onUpdateElement}
              onUpdateDots={onUpdateDots}
              onUpdateColumns={onUpdateColumns}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Child Element (Chord / Tab) ── */

function ChildElement({
  element,
  readOnly,
  editingNoteId,
  onSetEditingNote,
  onDelete,
  onUpdateElement,
  onUpdateDots,
  onUpdateColumns,
  t,
}: {
  element: ChordElement | TabElement | TablatureElement | ImageElement | MidiElement
  readOnly?: boolean
  editingNoteId: string | null
  onSetEditingNote: (id: string | null) => void
  onDelete: () => void
  onUpdateElement: (id: string, data: Partial<SongElement>) => void
  onUpdateDots: (id: string, dots: TabDot[]) => void
  onUpdateColumns: (id: string, columns: TablatureColumn[]) => void
  t: ReturnType<typeof useI18n>['t']
}) {
  if (element.type === 'chord') {
    return (
      <div className="group relative shrink-0">
        <span
          className={`inline-block rounded border border-surface-lighter px-2.5 py-1 text-base font-bold text-text ${!readOnly ? 'cursor-pointer active:border-error active:text-error' : ''}`}
          onClick={readOnly ? undefined : onDelete}
        >
          {element.name.charAt(0).toUpperCase() + element.name.slice(1)}
        </span>
        {element.note && (
          <p className="mt-0.5 text-[15px] italic text-text-muted">{element.note}</p>
        )}
        {!readOnly && <div className="absolute -right-1 -top-1 hidden gap-0.5 rounded bg-surface-light group-hover:flex">
          <button className="rounded p-0.5 text-text-muted hover:text-text"
            onClick={() => onSetEditingNote(editingNoteId === element.id ? null : element.id)}>
            <PenIcon size={10} />
          </button>
          <button className="rounded p-0.5 text-text-muted hover:text-error" onClick={onDelete}>
            <XIcon size={10} />
          </button>
        </div>}
        {!readOnly && editingNoteId === element.id && (
          <NoteInput
            value={element.note ?? ''}
            onChange={(v) => onUpdateElement(element.id, { note: v })}
            onClose={() => onSetEditingNote(null)}
            t={t}
          />
        )}
      </div>
    )
  }

  // Tablature element
  if (element.type === 'tablature') {
    return (
      <div className="relative w-full">
        <div className="flex items-start gap-1">
          <div className="flex-1">
            <GuitarTablature
              columns={element.columns}
              onChange={readOnly ? undefined : (cols) => onUpdateColumns(element.id, cols)}
            />
          </div>
          {!readOnly && <div className="flex flex-col gap-0.5 pt-1">
            <button className="rounded p-0.5 text-text-muted hover:text-text"
              onClick={() => onSetEditingNote(editingNoteId === element.id ? null : element.id)}>
              <PenIcon size={12} />
            </button>
            <button className="rounded p-0.5 text-text-muted hover:text-error" onClick={onDelete}>
              <XIcon size={12} />
            </button>
          </div>}
        </div>
        {element.note && (
          <p className="mt-0.5 text-[15px] italic text-text-muted">{element.note}</p>
        )}
        {!readOnly && editingNoteId === element.id && (
          <NoteInput
            value={element.note ?? ''}
            onChange={(v) => onUpdateElement(element.id, { note: v })}
            onClose={() => onSetEditingNote(null)}
            t={t}
          />
        )}
      </div>
    )
  }

  // MIDI element
  if (element.type === 'midi') {
    return (
      <div className="relative w-full">
        <div className="flex items-start gap-1">
          <div className="flex-1">
            <MidiPlayer
              element={element}
              onTempoChange={readOnly ? undefined : (tempo) => onUpdateElement(element.id, { tempo })}
            />
          </div>
          {!readOnly && <div className="flex flex-col gap-0.5 pt-1">
            <button className="rounded p-0.5 text-text-muted hover:text-text"
              onClick={() => onSetEditingNote(editingNoteId === element.id ? null : element.id)}>
              <PenIcon size={12} />
            </button>
            <button className="rounded p-0.5 text-text-muted hover:text-error" onClick={onDelete}>
              <XIcon size={12} />
            </button>
          </div>}
        </div>
        {element.note && (
          <p className="mt-0.5 text-[15px] italic text-text-muted">{element.note}</p>
        )}
        {!readOnly && editingNoteId === element.id && (
          <NoteInput
            value={element.note ?? ''}
            onChange={(v) => onUpdateElement(element.id, { note: v })}
            onClose={() => onSetEditingNote(null)}
            t={t}
          />
        )}
      </div>
    )
  }

  // Image element
  if (element.type === 'image') {
    return (
      <ImageChild
        element={element}
        readOnly={readOnly}
        editingNoteId={editingNoteId}
        onSetEditingNote={onSetEditingNote}
        onDelete={onDelete}
        onUpdateElement={onUpdateElement}
        t={t}
      />
    )
  }

  // Tab element (chord diagram)
  return (
    <div className="relative shrink-0">
      <div className="flex items-start gap-1">
        <GuitarTab
          dots={element.dots}
          startFret={element.startFret ?? 1}
          onChange={readOnly ? undefined : (dots) => onUpdateDots(element.id, dots)}
          onStartFretChange={readOnly ? undefined : (fret) => onUpdateElement(element.id, { startFret: fret })}
          size="md"
        />
        {!readOnly && <div className="flex flex-col gap-0.5 pt-1">
          <button className="rounded p-0.5 text-text-muted hover:text-text"
            onClick={() => onSetEditingNote(editingNoteId === element.id ? null : element.id)}>
            <PenIcon size={12} />
          </button>
          <button className="rounded p-0.5 text-text-muted hover:text-error" onClick={onDelete}>
            <XIcon size={12} />
          </button>
        </div>}
      </div>
      {element.note && (
        <p className="mt-0.5 text-[15px] italic text-text-muted">{element.note}</p>
      )}
      {editingNoteId === element.id && (
        <NoteInput
          value={element.note ?? ''}
          onChange={(v) => onUpdateElement(element.id, { note: v })}
          onClose={() => onSetEditingNote(null)}
          t={t}
        />
      )}
    </div>
  )
}

/* ── Image element with signed URL ── */

function ImageChild({
  element,
  readOnly,
  editingNoteId,
  onSetEditingNote,
  onDelete,
  onUpdateElement,
  t,
}: {
  element: ImageElement
  readOnly?: boolean
  editingNoteId: string | null
  onSetEditingNote: (id: string | null) => void
  onDelete: () => void
  onUpdateElement: (id: string, data: Partial<SongElement>) => void
  t: ReturnType<typeof useI18n>['t']
}) {
  const imgUrl = useCachedImage(element.storagePath)

  if (!imgUrl) return <div className="w-full py-4 text-center text-xs text-text-muted">...</div>

  return (
    <div className="relative w-full">
      <img
        src={imgUrl}
        alt={element.note || ''}
        style={{ maxHeight: IMAGE_MAX_HEIGHT, width: 'auto' }}
        className="rounded"
      />
      {!readOnly && <div className="absolute right-1 top-1 flex gap-0.5 rounded bg-surface/80 p-0.5">
        <button className="rounded p-0.5 text-text-muted hover:text-text"
          onClick={() => onSetEditingNote(editingNoteId === element.id ? null : element.id)}>
          <PenIcon size={12} />
        </button>
        <button className="rounded p-0.5 text-text-muted hover:text-error" onClick={onDelete}>
          <XIcon size={12} />
        </button>
      </div>}
      {element.note && (
        <p className="mt-0.5 text-[15px] italic text-text-muted">{element.note}</p>
      )}
      {!readOnly && editingNoteId === element.id && (
        <NoteInput
          value={element.note ?? ''}
          onChange={(v) => onUpdateElement(element.id, { note: v })}
          onClose={() => onSetEditingNote(null)}
          t={t}
        />
      )}
    </div>
  )
}

/* ── Section content indicator badges (shown when collapsed) ── */

function SectionContentBadges({ tracks, t }: {
  tracks: InstrumentTrack[]
  t: ReturnType<typeof useI18n>['t']
}) {
  const named = tracks.filter((tr) => tr.instrument.instrumentTypeId !== '_default')
  const hasDefaultContent = tracks.some(
    (tr) => tr.instrument.instrumentTypeId === '_default' && tr.children.length > 0
  )

  if (named.length === 0 && !hasDefaultContent) return null

  return (
    <>
      {named.map((tr) => {
        const instCfg = INSTRUMENT_TYPES.find((i) => i.id === tr.instrument.instrumentTypeId)
        const instLabel = tr.instrument.customName
          || (instCfg ? t(instCfg.i18nKey as Parameters<typeof t>[0]) : tr.instrument.instrumentTypeId)
        return (
          <span
            key={tr.instrument.id}
            className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] leading-none text-white/60"
          >
            {instLabel}
          </span>
        )
      })}
      {hasDefaultContent && named.length === 0 && (
        <span className="text-[10px] leading-none text-white/50">•</span>
      )}
    </>
  )
}

/* ── Shared small components ── */

function NoteInput({ value, onChange, onClose, t }: {
  value: string
  onChange: (v: string) => void
  onClose: () => void
  t: ReturnType<typeof useI18n>['t']
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onClose}
      onKeyDown={(e) => e.key === 'Enter' && onClose()}
      placeholder={t('songbook.elementNotePlaceholder')}
      className="mt-1 w-full rounded border border-surface-lighter bg-surface px-2 py-0.5 text-xs text-text placeholder-text-muted focus:border-primary focus:outline-none"
      autoFocus
    />
  )
}

function ActionIcons({ onNote, onMoveUp, onMoveDown, onDelete, canMoveUp, canMoveDown }: {
  onNote: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onDelete: () => void
  canMoveUp: boolean
  canMoveDown: boolean
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <button className="rounded p-1 text-text-muted hover:text-text" onClick={onNote}>
        <PenIcon size={14} />
      </button>
      <button className="rounded p-1 text-text-muted hover:text-text disabled:opacity-30"
        onClick={onMoveUp} disabled={!canMoveUp}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m18 15-6-6-6 6"/></svg>
      </button>
      <button className="rounded p-1 text-text-muted hover:text-text disabled:opacity-30"
        onClick={onMoveDown} disabled={!canMoveDown}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      <button className="rounded p-1 text-text-muted hover:text-error" onClick={onDelete}>
        <XIcon size={14} />
      </button>
    </div>
  )
}

function PenIcon({ size = 14 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/>
    </svg>
  )
}

function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
    </svg>
  )
}
